import { Injectable, OnDestroy, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Subscription, from, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, take } from 'rxjs/operators';
import { AppInsightsService } from './app-insights.service';
import { ClientTelemetryContextService } from './client-telemetry-context.service';
import { isNativeApp } from '../auth/native-auth';

/**
 * Emits structured startup + auth-lifecycle breadcrumbs to Application Insights
 * so the cold-start "tokenless window" (spec 034) becomes visible in telemetry.
 *
 * Background: the pilot's native app hangs on cold start and the backend logs a
 * burst of `403 Not authenticated` on `GET /v2/patients` — a 403 (not 401) from
 * FastAPI `HTTPBearer()` means the Authorization header was ENTIRELY MISSING, so
 * the client is emitting tokenless requests during an Auth0 session-restoration
 * race. This service does NOT fix that (that is a later, separate slice). It
 * only instruments the auth lifecycle so we can PROVE the window exists and
 * measure it, correlated to the backend via `X-Rettx-Correlation-Id`.
 *
 * Everything here is observability only: it never gates a request, never changes
 * the Auth0 config/flow, and never lets a telemetry failure surface to the app.
 * The one active call it makes — a single `getAccessTokenSilently()` PROBE once
 * the SDK reports authenticated — mirrors exactly what the existing Auth0 HTTP
 * interceptor already does when attaching a bearer token; it merely times it and
 * records start/success/fail. The token value itself is NEVER logged.
 */
@Injectable({ providedIn: 'root' })
export class StartupTelemetryService implements OnDestroy {
  private auth = inject(AuthService);
  private appInsights = inject(AppInsightsService);
  private telemetryContext = inject(ClientTelemetryContextService);

  private initialized = false;
  private readonly subscriptions = new Subscription();
  private authRestoreStartEmitted = false;
  private authRestoreSettled = false;
  private tokenProbeDone = false;
  private readonly startedAtMs = Date.now();

  /**
   * Begin emitting startup/auth breadcrumbs. Idempotent. Call once, early, from
   * the root component.
   */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.appInsights.trackTrace('startup_begin', {
      platform: isNativeApp() ? 'native' : 'web',
      appVersion: this.telemetryContext.appVersion,
    });
    // Best-effort flush so this first breadcrumb survives an immediate hang.
    this.appInsights.flush();

    this.observeAuthLifecycle();
    this.observeAuthErrors();
  }

  /**
   * Watch the Auth0 SDK's `isLoading$` -> `isAuthenticated$` transition, which
   * IS the session-restoration window we care about, and breadcrumb its edges.
   */
  private observeAuthLifecycle(): void {
    // First `isLoading` emission marks restore start.
    this.subscriptions.add(
      this.auth.isLoading$
        .pipe(distinctUntilChanged())
        .subscribe((isLoading) => {
          if (isLoading && !this.authRestoreStartEmitted) {
            this.authRestoreStartEmitted = true;
            this.appInsights.trackTrace('auth_restore_start', {
              elapsedMs: Date.now() - this.startedAtMs,
            });
          }
        }),
    );

    // When loading settles, record whether a session was restored, then probe
    // for a token if authenticated.
    this.subscriptions.add(
      this.auth.isLoading$
        .pipe(
          filter((isLoading) => !isLoading),
          take(1),
          switchMap(() => this.auth.isAuthenticated$.pipe(take(1))),
        )
        .subscribe((isAuthenticated) => {
          if (this.authRestoreSettled) {
            return;
          }
          this.authRestoreSettled = true;
          const elapsedMs = Date.now() - this.startedAtMs;
          if (isAuthenticated) {
            this.appInsights.trackTrace('auth_restore_success', { elapsedMs });
            this.probeAccessToken();
          } else {
            this.appInsights.trackTrace('auth_restore_unauthenticated', { elapsedMs });
          }
          this.appInsights.flush();
        }),
    );
  }

  /**
   * Single silent-token acquisition probe. Times `getAccessTokenSilently()` and
   * records start/success/fail — this is precisely the call whose absence causes
   * the tokenless request. Never logs the token; never rethrows.
   */
  private probeAccessToken(): void {
    if (this.tokenProbeDone) {
      return;
    }
    this.tokenProbeDone = true;

    const startMs = Date.now();
    this.appInsights.trackTrace('access_token_acquire_start', {
      elapsedMs: startMs - this.startedAtMs,
    });

    this.subscriptions.add(
      from(this.auth.getAccessTokenSilently())
        .pipe(
          catchError((err) => {
            this.appInsights.trackTrace('access_token_acquire_fail', {
              elapsedMs: Date.now() - this.startedAtMs,
              durationMs: Date.now() - startMs,
              errorName: (err as { error?: string; name?: string })?.error ??
                (err as Error)?.name ?? 'UnknownError',
              errorMessage: (err as Error)?.message,
            });
            this.appInsights.flush();
            return of(null);
          }),
        )
        .subscribe((token) => {
          if (token) {
            this.appInsights.trackTrace('access_token_acquire_success', {
              elapsedMs: Date.now() - this.startedAtMs,
              durationMs: Date.now() - startMs,
            });
            this.appInsights.flush();
          }
        }),
    );
  }

  /** Breadcrumb Auth0 errors (e.g. login_required / consent_required). */
  private observeAuthErrors(): void {
    this.subscriptions.add(
      this.auth.error$.subscribe((error) => {
        const err = error as { error?: string; message?: string; name?: string };
        this.appInsights.trackTrace('auth_restore_fail', {
          elapsedMs: Date.now() - this.startedAtMs,
          errorName: err?.error ?? err?.name ?? 'UnknownError',
          errorMessage: err?.message,
        });
        this.appInsights.flush();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
